import React,{createContext,useCallback,useContext,useEffect,useMemo,useRef,useState} from 'react';
const OrganizationContext=createContext(null);
const ACTIVE_ORGANIZATION_KEY='cybedefender.activeOrganizationGuid';
export function OrganizationProvider({isAuthenticated,onAuthenticationFailure,children}) {
 const [organizations,setOrganizations]=useState([]),[activeOrganization,setActiveOrganization]=useState(null);
 const [organizationLoading,setOrganizationLoading]=useState(false),[organizationError,setOrganizationError]=useState('');
 const [loadedToken,setLoadedToken]=useState(null);
 const version=useRef(0),selectedGuid=useRef(null),controller=useRef(null);
 const refreshOrganizations=useCallback(async()=>{
  const generation=++version.current;
  controller.current?.abort();
  const token=isAuthenticated?localStorage.getItem('token'):null;
  if(!token){setOrganizations([]);setActiveOrganization(null);setLoadedToken(null);setOrganizationLoading(false);setOrganizationError('');return;}
  const abort=new AbortController();controller.current=abort;
  const current=()=>!abort.signal.aborted&&generation===version.current&&token===localStorage.getItem('token');
  setOrganizationLoading(true);setOrganizationError('');
  try {
   const base=process.env.REACT_APP_CYBEDEFENDER_AI_URL||'/api';
   const response=await fetch(base+'/cybedefender/organizations',{headers:{Authorization:'Bearer '+token},signal:abort.signal});
   if(!current())return;
   if(response.status===401||response.status===422){onAuthenticationFailure?.();return;}
   if(!response.ok)throw new Error('Unable to load your workspaces.');
   const data=await response.json();if(!current())return;
   const rows=Array.isArray(data.organizations)?data.organizations.filter(o=>o?.guid&&['admin','analyst','viewer'].includes(o.membership_role)):[];
   const guid=selectedGuid.current||localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
   const selected=rows.find(o=>o.guid===guid)||rows[0]||null;
   selectedGuid.current=selected?.guid||null;setOrganizations(rows);setActiveOrganization(selected);
   if(selected)localStorage.setItem(ACTIVE_ORGANIZATION_KEY,selected.guid);else localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  }catch(error){if(current()){setOrganizations([]);setActiveOrganization(null);setOrganizationError('Unable to load your workspaces.');}}
  finally{if(current()){setLoadedToken(token);setOrganizationLoading(false);}}
 },[isAuthenticated,onAuthenticationFailure]);
 useEffect(()=>{
  selectedGuid.current=null;setLoadedToken(null);
  if(!isAuthenticated)localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  refreshOrganizations();
  return()=>{controller.current?.abort();version.current+=1;};
 },[isAuthenticated,refreshOrganizations]);
 useEffect(()=>{
  if(!isAuthenticated)return;
  const sync=()=>{if(document.visibilityState==='visible'&&!controller.current?.signal.aborted)refreshOrganizations();};
  window.addEventListener('focus',sync);
  const timer=setInterval(sync,30000);
  return()=>{window.removeEventListener('focus',sync);clearInterval(timer);};
 },[isAuthenticated,refreshOrganizations]);
 const selectOrganization=useCallback(guid=>{
  const selected=organizations.find(o=>o.guid===guid);if(!selected)return false;
  selectedGuid.current=guid;setActiveOrganization(selected);localStorage.setItem(ACTIVE_ORGANIZATION_KEY,guid);return true;
 },[organizations]);
 const organizationReady=Boolean(isAuthenticated&&loadedToken&&loadedToken===localStorage.getItem('token'));
 const value=useMemo(()=>({organizations,activeOrganization,organizationLoading,organizationReady,organizationError,selectOrganization,refreshOrganizations}),[organizations,activeOrganization,organizationLoading,organizationReady,organizationError,selectOrganization,refreshOrganizations]);
 return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}
export function useOrganization(){const context=useContext(OrganizationContext);if(!context)throw new Error('useOrganization must be used inside OrganizationProvider');return context;}
export {ACTIVE_ORGANIZATION_KEY};
